import { Theme } from '@mui/material';
import {
	DriverAverageScoresDto,
	DriverScoreInsightsDto,
} from '@acorn-portal/shared/src/services/telematicsDriverScoreApi';

/**
 * Time period options for filtering driver scores.
 */
export type TimePeriod = 'overall' | 'week' | 'month';

/**
 * Represents individual score values for driver metrics.
 */
export interface ScoreValues {
	/** Total/overall score (0-100). */
	total: number;
	/** Smoothness score (0-100). */
	smoothness: number;
	/** Speed score (0-100). */
	speed: number;
	/** Usage score (0-100). */
	usage: number;
}

/**
 * Properties for rendering an SVG circular gauge.
 */
export interface GaugeProperties {
	/** Radius of the gauge circle. */
	radius: number;
	/** Total circumference of the circle. */
	circumference: number;
	/** Progress value clamped between 0 and 1. */
	progress: number;
	/** Stroke dash offset for animating the progress arc. */
	strokeDashoffset: number;
}

/**
 * Returns the appropriate theme color based on a score value.
 *
 * Color thresholds:
 * - score >= 70: success (green)
 * - score >= 50: warning (amber)
 * - score < 50: error (red)
 *
 * @param score - Score value (0-100)
 * @param theme - MUI theme object for accessing palette colors
 * @returns Theme color string from the palette
 *
 * @example
 * ```ts
 * const color = getScoreColor(85, theme); // Returns theme.palette.success.main
 * ```
 */
export const getScoreColor = (score: number, theme: Theme): string => {
	if (score >= 70) return theme.palette.success.main;
	if (score >= 50) return theme.palette.warning.main;
	return theme.palette.error.main;
};

/**
 * Extracts score values from API responses based on the selected time period.
 *
 * For 'overall' period, uses averageScores data.
 * For 'week' or 'month' periods, uses insights currentAverages data.
 *
 * @param timePeriod - The selected time period filter
 * @param averageScores - Overall average scores from the API (nullable)
 * @param insights - Period-specific insights from the API (nullable)
 * @returns Object containing total, smoothness, speed, and usage scores
 *
 * @example
 * ```ts
 * const scores = extractScoresFromResponse('overall', averageScores, null);
 * console.log(scores.total); // e.g., 75
 * ```
 */
export const extractScoresFromResponse = (
	timePeriod: TimePeriod,
	averageScores: DriverAverageScoresDto | null,
	insights: DriverScoreInsightsDto | null,
): ScoreValues => {
	if (timePeriod === 'overall' && averageScores?.averages) {
		return {
			total: averageScores.averages.totalScoreAvg ?? 0,
			smoothness: averageScores.averages.smoothScoreAvg ?? 0,
			speed: averageScores.averages.speedScoreAvg ?? 0,
			usage: averageScores.averages.useScoreAvg ?? 0,
		};
	}

	if (timePeriod !== 'overall' && insights?.currentAverages) {
		return {
			total: insights.currentAverages.totalScoreAvg ?? 0,
			smoothness: insights.currentAverages.smoothScoreAvg ?? 0,
			speed: insights.currentAverages.speedScoreAvg ?? 0,
			usage: insights.currentAverages.useScoreAvg ?? 0,
		};
	}

	return { total: 0, smoothness: 0, speed: 0, usage: 0 };
};

/**
 * Calculates SVG properties for rendering a circular progress gauge.
 *
 * The gauge uses SVG stroke-dasharray and stroke-dashoffset techniques
 * to create a partial circle that represents progress.
 *
 * @param score - The score value (0-100) to display
 * @param size - The width/height of the gauge in pixels
 * @param strokeWidth - The thickness of the gauge stroke in pixels
 * @returns Object containing radius, circumference, progress, and strokeDashoffset
 *
 * @example
 * ```ts
 * const props = calculateGaugeProperties(75, 120, 8);
 * // props.progress = 0.75
 * // props.strokeDashoffset = circumference * 0.25
 * ```
 */
export const calculateGaugeProperties = (score: number, size: number, strokeWidth: number): GaugeProperties => {
	const radius = (size - strokeWidth) / 2;
	const circumference = 2 * Math.PI * radius;
	const progress = Math.min(Math.max(score, 0), 100) / 100;
	const strokeDashoffset = circumference * (1 - progress);

	return {
		radius,
		circumference,
		progress,
		strokeDashoffset,
	};
};

/**
 * Converts a time period to the number of days for API requests.
 *
 * @param timePeriod - The selected time period
 * @returns Number of days (7 for week, 30 for month, 0 for overall)
 *
 * @example
 * ```ts
 * const days = getPeriodDays('week'); // Returns 7
 * ```
 */
export const getPeriodDays = (timePeriod: TimePeriod): number => {
	switch (timePeriod) {
		case 'week':
			return 7;
		case 'month':
			return 30;
		default:
			return 0;
	}
};

/**
 * Checks if the driver has no score data for the selected period.
 *
 * Returns true when all scores are 0, indicating no trips have been
 * recorded for that time period.
 *
 * @param scores - The score values to check
 * @returns true if there's no score data (all scores are 0)
 *
 * @example
 * ```ts
 * const hasNoData = hasNoScoreData({ total: 0, smoothness: 0, speed: 0, usage: 0 }); // true
 * const hasData = hasNoScoreData({ total: 85, smoothness: 90, speed: 80, usage: 75 }); // false
 * ```
 */
export const hasNoScoreData = (scores: ScoreValues): boolean => {
	return scores.total === 0 && scores.smoothness === 0 && scores.speed === 0 && scores.usage === 0;
};

/**
 * Checks if no trips were used in the average score calculation.
 *
 * Returns true when tripCountUsed is undefined, null, or 0, indicating
 * that no trips were available for calculating the driver scores.
 *
 * @param averageScores - The average scores DTO from the API (nullable)
 * @returns true if no trips were used in the calculation
 *
 * @example
 * ```ts
 * const noTrips = hasNoTripsUsed({ tripCountUsed: 0 }); // true
 * const hasTrips = hasNoTripsUsed({ tripCountUsed: 15 }); // false
 * ```
 */
export const hasNoTripsUsed = (averageScores: DriverAverageScoresDto | null): boolean => {
	return averageScores?.tripCountUsed === undefined || averageScores?.tripCountUsed === 0;
};

/**
 * Determines if the driver score card should display the empty state.
 *
 * Returns true when:
 * - No trips were used in the calculation, OR
 * - Data has finished loading and there's no score data available
 *
 * @param averageScores - The average scores DTO from the API (nullable)
 * @param isLoading - Whether the data is currently being loaded
 * @param scores - The extracted score values to check
 * @returns true if the empty state should be displayed
 *
 * @example
 * ```ts
 * const showEmpty = shouldShowEmptyState(averageScores, false, scores);
 * ```
 */
export const shouldShowEmptyState = (
	averageScores: DriverAverageScoresDto | null,
	isLoading: boolean,
	scores: ScoreValues,
): boolean => {
	return hasNoTripsUsed(averageScores) || (!isLoading && hasNoScoreData(scores));
};
